import React, { useState, useRef } from 'react';
import { HiClipboardCopy, HiDownload, HiCheck, HiEye, HiCode } from 'react-icons/hi';
import MermaidDiagram from './MermaidDiagram';
import './CodeBlock.css';

/**
 * CodeBlock Component - Renders code blocks with copy and download functionality
 * Special handling for Mermaid diagrams to render compiled graphs with zoom and pan
 * @param {Object} props - Component props
 * @param {string} props.children - The code content
 * @param {string} props.className - CSS class name (usually contains language info)
 * @param {string} props.language - Programming language for syntax highlighting
 * @param {boolean} props.isStreaming - Whether the code content is still streaming
 */
const CodeBlock = ({ children, className, language, isStreaming = false, ...props }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showMermaidCode, setShowMermaidCode] = useState(false);
  
  const codeRef = useRef(null);

  // Extract language from className (format: language-javascript, language-python, etc.)
  const match = /language-(\w+)/.exec(className || '');
  const detectedLanguage = match ? match[1] : language || 'text';
  
  // Get the actual code content
  const codeContent = String(children).replace(/\n$/, '');

  // Check if this is a mermaid diagram
  const isMermaid = detectedLanguage.toLowerCase() === 'mermaid';

  // Handle copy functionality
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = codeContent;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle download functionality
  const handleDownload = () => {
    setDownloading(true);
    
    try {
      // Determine file extension based on language
      const getFileExtension = (lang) => {
        const extensions = {
          javascript: 'js',
          typescript: 'ts',
          python: 'py',
          java: 'java',
          cpp: 'cpp',
          c: 'c',
          csharp: 'cs',
          php: 'php',
          ruby: 'rb',
          go: 'go',
          rust: 'rs',
          swift: 'swift',
          kotlin: 'kt',
          scala: 'scala',
          html: 'html',
          css: 'css',
          scss: 'scss',
          sass: 'sass',
          less: 'less',
          xml: 'xml',
          json: 'json',
          yaml: 'yml',
          yml: 'yml',
          markdown: 'md',
          sql: 'sql',
          bash: 'sh',
          shell: 'sh',
          powershell: 'ps1',
          dockerfile: 'dockerfile',
          r: 'r',
          matlab: 'm',
          perl: 'pl',
          lua: 'lua',
          dart: 'dart',
          vue: 'vue',
          jsx: 'jsx',
          tsx: 'tsx',
          mermaid: 'mmd'
        };
        return extensions[lang.toLowerCase()] || 'txt';
      };

      const fileExtension = getFileExtension(detectedLanguage);
      const fileName = `code-snippet.${fileExtension}`;
      
      // Create blob and download
      const blob = new Blob([codeContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download code:', err);
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  // Toggle between mermaid diagram and code view
  const toggleMermaidView = () => {
    setShowMermaidCode(!showMermaidCode);
  };

  // Count lines for line numbers (only for code view)
  const lines = codeContent.split('\n');
  const showLineNumbers = lines.length > 5 && (!isMermaid || showMermaidCode);

  return (
    <div className="code-block-container relative group my-4 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      {/* Header with language and action buttons */}
      <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">
          {detectedLanguage}
          
        </span>
        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          {/* Mermaid View Toggle Button */}
          {isMermaid && (
            <button
              onClick={toggleMermaidView}
              data-toggle-code
              className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 rounded transition-colors duration-200 text-blue-700 dark:text-blue-200"
              title={showMermaidCode ? "Show diagram" : "Show code"}
            >
              {showMermaidCode ? (
                <>
                  <HiEye className="w-3 h-3" />
                  <span>Diagram</span>
                </>
              ) : (
                <>
                  <HiCode className="w-3 h-3" />
                  <span>Code</span>
                </>
              )}
            </button>
          )}
          
          {/* Copy Button */}
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors duration-200 text-gray-700 dark:text-gray-300"
            title="Copy code"
            disabled={copied}
          >
            {copied ? (
              <>
                <HiCheck className="w-3 h-3 text-green-500" />
                <span className="text-green-500">Copied!</span>
              </>
            ) : (
              <>
                <HiClipboardCopy className="w-3 h-3" />
                <span>Copy</span>
              </>
            )}
          </button>
          
          {/* Download Button */}
          <button
            onClick={handleDownload}
            className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors duration-200 text-gray-700 dark:text-gray-300"
            title="Download code"
            disabled={downloading}
          >
            <HiDownload className={`w-3 h-3 ${downloading ? 'animate-bounce' : ''}`} />
            <span>{downloading ? 'Downloading...' : 'Download'}</span>
          </button>
        </div>
      </div>
      
      {/* Content - either mermaid diagram or code */}
      <div className="relative bg-gray-50 dark:bg-gray-950">
        {isMermaid && !showMermaidCode ? (
          /* Mermaid diagram view with zoom and pan */
          <MermaidDiagram 
            code={codeContent}
            onShowCode={() => setShowMermaidCode(true)}
            isStreaming={isStreaming}
          />
        ) : (
          /* Code view */
          <div>
            <pre 
              className="overflow-x-auto p-4 text-sm leading-relaxed"
              ref={codeRef}
              {...props}
            >
              <code className="text-gray-900 dark:text-white font-mono">
                {showLineNumbers ? (
                  <div className="flex">
                    {/* Line numbers */}
                    <div className="select-none text-gray-400 dark:text-gray-500 pr-4 border-r border-gray-300 dark:border-gray-600 mr-4 text-right min-w-[2rem] flex flex-col">
                      {lines.map((_, index) => (
                        <div key={index} className="leading-relaxed py-0 h-[1.5rem] flex items-center justify-end">
                          {index + 1}
                        </div>
                      ))}
                    </div>
                    {/* Code content */}
                    <div className="flex-1 text-gray-900 dark:text-white flex flex-col">
                      {lines.map((line, index) => (
                        <div key={index} className="leading-relaxed py-0 h-[1.5rem] flex items-center" style={{ whiteSpace: 'pre' }}>
                          {line || '\u00A0'} {/* Non-breaking space for empty lines */}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  codeContent
                )}
              </code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeBlock; 